<template>
  <section>
    <DatePickerWithValidation
      v-model="debutInscription"
      vid="debutInscription"
      rules="required"
      :label="$t('ParametersPage.inscription.date-ouverture')"
      :placeholder="$t('ParametersPage.selectionner-date')"
      :disabled="readOnlyInscription"
      @manual-change="dateChanged"
    />
    <DatePickerWithValidation
      v-model="finInscription"
      vid="finInscription"
      :rules="`${debutInscription ? `dateIsAfter:${debutInscription.toString()}` : ''}|required`"
      :label="$t('ParametersPage.inscription.date-fermeture')"
      :placeholder="$t('ParametersPage.selectionner-date')"
      :disabled="readOnly"
      @manual-change="dateChanged"
    />

    <DatePickerWithValidation
      v-model="debutLabellisation"
      vid="debutLabellisation"
      rules="required"
      :label="$t('ParametersPage.labellisation.date-ouverture')"
      :placeholder="$t('ParametersPage.selectionner-date')"
      :disabled="readOnly"
      @manual-change="dateChanged"
    />
    <DatePickerWithValidation
      v-model="finLabellisation"
      vid="finLabellisation"
      :rules="`${
        debutLabellisation ? `dateIsAfter:${debutLabellisation.toString()}` : ''
      }|required`"
      :label="$t('ParametersPage.labellisation.date-fermeture')"
      :placeholder="$t('ParametersPage.selectionner-date')"
      :disabled="readOnly"
      @manual-change="dateChanged"
    />

    <InputWithValidation
      v-model="debutAnneeScolaire"
      :label="$t(`ParametersPage.debut-annee-scolaire`)"
      :disabled="true"
      rules="required"
      vid="debutAnneeScolaire"
      type="text"
    />

    <div v-if="!readOnly" class="buttons">
      <b-button type="is-primary is-rounded" :disabled="!dirty" @click="submitParametrage()">
        {{ $t("ParametersPage.valider-dates") }}
      </b-button>
      <b-button type="is-primary is-outlined is-rounded" @click="resetParametrage()">
        {{ $t("ParametersPage.reset-dates") }}
      </b-button>
      <b-button
        v-if="isApplicationDateDevModeActivated"
        type="is-danger is-outlined is-rounded"
        @click="moveToNextYear()"
      >
        {{ $t("ParametersPage.simulate-fin-annee-scolaire") }}
      </b-button>
    </div>
    <b-loading v-model="isLoading" is-full-page />
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import DatePickerWithValidation from "@/views/inputs/DatePickerWithValidation.vue"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import { GraeDTO, ParametrageDTO } from "@/model/bean/GeneratedDTOs"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import { ParametersService } from "@/services/rest/parameters/ParametersService"
import { GraeService } from "@/services/rest/grae/GraeService"
import { UsersService } from "@/services/rest/users/UsersService"
import { LoginService } from "@/services/rest/login/LoginService"
import ConversionUtils from "@/services/ConversionUtils"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { ApplicationDateService } from "@/services/rest/utils/ApplicationDateService"
import { BButton, BLoading } from "buefy"

export default defineComponent({
  name: "GraeDatesView",
  components: {
    DatePickerWithValidation,
    InputWithValidation,
    BButton,
    BLoading,
  },
  props: {
    grae: {
      type: Object as PropType<GraeDTO>,
      required: false,
    },
  },
  data() {
    return {
      infoReportingService: InfoReportingService.INSTANCE,
      applicationDateService: ApplicationDateService.INSTANCE,
      parametrageService: ParametersService.INSTANCE,
      graeService: GraeService.INSTANCE,
      userService: UsersService.INSTANCE,
      loginService: LoginService.INSTANCE,

      usedGrae: {} as GraeDTO,
      debutInscription: new Date(),
      finInscription: new Date(),
      debutLabellisation: new Date(),
      finLabellisation: new Date(),
      defaultParametrage: null as ParametrageDTO | null,
      readOnly: true,
      readOnlyInscription: true,
      dirty: false,
      isLoading: false,
      isApplicationDateDevModeActivated: false,
      debutAnneeScolaire: "",
    }
  },
  async created(): Promise<void> {
    this.usedGrae = Object.assign({}, this.grae)
    await this.initParametrage()

    const loggedUser = this.loginService.getLoggedUser()
    if (loggedUser) {
      this.readOnly = !this.userService.canModifyGrae(loggedUser, this.usedGrae.id)
      this.readOnlyInscription =
        !this.userService.canModifyGrae(loggedUser, this.usedGrae.id) ||
        loggedUser.profil == PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL
    }
    this.dirty = false
  },
  methods: {
    dateChanged(): void {
      this.dirty = true
    },

    //Create parametrage is needed and than copy form values and submit
    async submitParametrage(): Promise<void> {
      if (this.usedGrae.parametrage) {
        await this.copyAndSubmit()
      } else if (
        this.defaultParametrage !== null &&
        (this.defaultParametrage.debutInscription !== this.debutInscription ||
          this.defaultParametrage.finInscription !== this.finInscription ||
          this.defaultParametrage.debutLabellisation !== this.debutLabellisation ||
          this.defaultParametrage.finLabellisation !== this.finLabellisation)
      ) {
        this.usedGrae.parametrage = new ParametrageDTO()
        await this.copyAndSubmit()
      }
    },

    //Copy the form values and submit
    async copyAndSubmit(): Promise<void> {
      if (this.usedGrae.parametrage) {
        this.usedGrae.parametrage.debutInscription = this.debutInscription
        this.usedGrae.parametrage.finInscription = this.finInscription
        this.usedGrae.parametrage.debutLabellisation = this.debutLabellisation
        this.usedGrae.parametrage.finLabellisation = this.finLabellisation

        this.usedGrae = await this.graeService.updateGrae(this.usedGrae)
        await this.initParametrage()
        this.dirty = false
      }
    },

    resetParametrage(): void {
      this.infoReportingService.dialog(
        this.$t("ParametersPage.confirm-title").toString(),
        this.$t("ParametersPage.confirm-question").toString(),
        this.$t("ParametersPage.confirm-action").toString(),
        () => this.doResetParametrage(),
        "is-info",
      )
    },

    async doResetParametrage(): Promise<void> {
      this.usedGrae = await this.graeService.resetGraeParametrage(this.usedGrae.id)
      await this.initParametrage()
      this.dirty = false
    },
    //Init the parametrage for the form
    async initParametrage(): Promise<void> {
      //get back default parametrage
      this.defaultParametrage = await this.parametrageService.getParametrage(this.usedGrae.id)

      if (this.usedGrae.parametrage !== undefined && this.usedGrae.parametrage !== null) {
        if (this.usedGrae.parametrage.debutInscription) {
          this.debutInscription = this.usedGrae.parametrage.debutInscription
        } else {
          this.debutInscription = this.defaultParametrage.debutInscription
        }

        if (this.usedGrae.parametrage.finInscription) {
          this.finInscription = this.usedGrae.parametrage.finInscription
        } else {
          this.finInscription = this.defaultParametrage.finInscription
        }

        if (this.usedGrae.parametrage.debutLabellisation) {
          this.debutLabellisation = this.usedGrae.parametrage.debutLabellisation
        } else {
          this.debutLabellisation = this.defaultParametrage.debutLabellisation
        }

        if (this.usedGrae.parametrage.finLabellisation) {
          this.finLabellisation = this.usedGrae.parametrage.finLabellisation
        } else {
          this.finLabellisation = this.defaultParametrage.finLabellisation
        }
      } else {
        //use full default parametrage
        this.debutInscription = this.defaultParametrage.debutInscription
        this.finInscription = this.defaultParametrage.finInscription
        this.debutLabellisation = this.defaultParametrage.debutLabellisation
        this.finLabellisation = this.defaultParametrage.finLabellisation
      }

      this.debutInscription = ConversionUtils.convertToUTCDate(this.debutInscription)
      this.finInscription = ConversionUtils.convertToUTCDate(this.finInscription)
      this.debutLabellisation = ConversionUtils.convertToUTCDate(this.debutLabellisation)
      this.finLabellisation = ConversionUtils.convertToUTCDate(this.finLabellisation)

      if (
        this.usedGrae.parametrage !== undefined &&
        this.usedGrae.parametrage !== null &&
        this.usedGrae.parametrage.moisDebutAnneeScolaire !== null &&
        this.usedGrae.parametrage.jourDebutAnneeScolaire !== undefined
      ) {
        this.debutAnneeScolaire = `${this.usedGrae.parametrage.jourDebutAnneeScolaire} ${this.usedGrae.parametrage.moisDebutAnneeScolaire}`
      } else {
        this.debutAnneeScolaire = `${this.defaultParametrage.jourDebutAnneeScolaire} ${this.defaultParametrage.moisDebutAnneeScolaire}`
      }

      try {
        await this.applicationDateService.getApplicationDate()
        this.isApplicationDateDevModeActivated = true
      } catch (e: any) {
        // Silent catch, applicationDevMod is not activated
      }
    },

    async moveToNextYear(): Promise<void> {
      this.infoReportingService.dialogWithCancel(
        "Simuler la fin d'année scolaire",
        "Simuler un changement d'année scolaire pour ce GRAE ? Tous les projets en cours seront décalés d'un an dans le passé et de nouveau projets seront créé pour l'année en cours.",
        "Oui",
        async () => {
          this.isLoading = true
          await this.applicationDateService.simulateAnneeScolaireEnd(this.grae.id)
          this.isLoading = false
        },
        () => {
          // Do nothing on cancel
        },
        "is-danger",
      )
    },
  },
})
</script>
